import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaUserCircle, FaUserLock } from "react-icons/fa";
import { GoPasskeyFill } from "react-icons/go";
import { LuTabletSmartphone } from "react-icons/lu";
import { toast } from "react-toastify";
import { useAuth } from "@/auth/useAuth";
import { deleteProfilePhoto, uploadProfilePhoto } from "@/features/account/services/profilePhotoService";
import { getAttachmentUrl } from "@/features/attachments/services/attachmentService";
import { FiCamera, FiTrash } from "react-icons/fi";
const AccountManagementPage = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { user, refreshUser } = useAuth();
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [profilePhotoUrl, setProfilePhotoUrl] = useState();
    const fileInputRef = useRef(null);
    useEffect(() => {
        if (state?.toast === "success") {
            toast.success(state.message);
        }
    }, [state]);
    useEffect(() => {
        if (!user?.profile_photo?.id) {
            setProfilePhotoUrl(undefined);
            return;
        }
        // Use direct URL instead of blob to avoid CSP issues
        const url = getAttachmentUrl(user.profile_photo.file_path);
        setProfilePhotoUrl(url);
    }, [user?.profile_photo?.id]);
    const handlePhotoChange = async (event) => {
        const file = event.target.files?.[0];
        if (!file)
            return;
        setIsUploading(true);
        try {
            await uploadProfilePhoto(file);
            await refreshUser();
            toast.success("Photo de profil mise à jour");
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Impossible de mettre à jour la photo.";
            toast.error(message);
        }
        finally {
            setIsUploading(false);
            event.target.value = "";
        }
    };
    const handleDeletePhoto = async () => {
        setIsDeleting(true);
        try {
            await deleteProfilePhoto();
            await refreshUser();
            toast.success("Photo de profil supprimée");
        }
        catch (error) {
            const message = error instanceof Error ? error.message : "Impossible de supprimer la photo.";
            toast.error(message);
        }
        finally {
            setIsDeleting(false);
        }
    };
    const cards = [
        {
            title: "Changer le mot de passe",
            description: "Utilisez une combinaison d’au moins 12 caractères, mêlant majuscules, minuscules, chiffres et symboles.",
            icon: _jsx(FaUserLock, { className: "w-8 h-8 text-gray-500" }),
            actionText: "Modifier mon mot de passe >",
            onClick: () => navigate("/account/change-password"),
        },
        {
            title: "Méthodes d’authentification",
            description: "Consultez les méthodes supplémentaires configurées pour accéder à votre compte, ajoutez-en de nouvelles ou révoquez-en certaines.",
            icon: _jsx(GoPasskeyFill, { className: "w-8 h-8 text-gray-500" }),
            actionText: "Gérer mes méthodes >",
            onClick: () => navigate("/account"),
        },
        {
            title: "Mes appareils",
            description: "Consultez les appareils liés à votre compte, vérifiez leur dernière connexion et révoquez-les si nécessaire.",
            icon: _jsx(LuTabletSmartphone, { className: "w-8 h-8 text-gray-500" }),
            actionText: "Gérer mes appareils >",
            onClick: () => navigate("/account"),
        },
    ];
    return (_jsxs("div", { className: "p-8 space-y-6", children: [_jsxs("div", { className: "space-y-4", children: [_jsxs("div", { className: "flex flex-col md:flex-row gap-6 bg-white border border-gray-200 rounded-lg shadow-sm p-6", children: [_jsxs("div", { className: "flex flex-wrap items-center gap-4 w-full md:w-auto", children: [_jsxs("div", { className: "relative pb-2", children: [_jsx("div", { className: "w-28 h-28 rounded-full bg-gray-100 border border-gray-200 overflow-hidden flex items-center justify-center", children: profilePhotoUrl ? (_jsx("img", { src: profilePhotoUrl, alt: "Photo de profil", className: "w-full h-full object-cover" })) : (_jsx(FaUserCircle, { className: "w-16 h-16 text-gray-400" })) }), _jsxs("div", { className: "absolute -bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2", children: [_jsxs("button", { type: "button", onClick: () => fileInputRef.current?.click(), disabled: isUploading || isDeleting, title: "Modifier la photo", className: "inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm text-blue-600 hover:bg-blue-50 disabled:opacity-70", children: [_jsx(FiCamera, { className: "w-4 h-4" }), _jsx("span", { className: "sr-only", children: isUploading ? "Enregistrement..." : "Modifier la photo" })] }), user?.profile_photo && (_jsxs("button", { type: "button", onClick: handleDeletePhoto, disabled: isUploading || isDeleting, title: "Supprimer la photo", className: "inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-gray-200 shadow-sm text-red-600 hover:bg-red-50 disabled:opacity-70", children: [_jsx(FiTrash, { className: "w-4 h-4" }), _jsx("span", { className: "sr-only", children: isDeleting ? "Suppression..." : "Supprimer la photo" })] }))] })] }), _jsxs("div", { children: [_jsxs("h1", { className: "text-2xl font-semibold text-gray-900", children: [user?.first_name, " ", user?.last_name] }), _jsx("p", { className: "text-gray-700 text-sm", children: user?.email })] })] }), _jsx("div", { className: "flex-1 flex flex-col justify-center gap-2", children: _jsx("input", { ref: fileInputRef, type: "file", accept: "image/*", className: "hidden", onChange: handlePhotoChange }) })] }), _jsxs("div", { children: [_jsx("h2", { className: "text-3xl font-semibold text-gray-900", children: "Informations de votre compte" }), _jsxs("p", { className: "mt-2 text-gray-600", children: ["Ces donn\u00E9es sont li\u00E9es \u00E0 votre compte et partag\u00E9es entre les diff\u00E9rents espaces de travail dont vous \u00EAtes membre. ", _jsx("br", {}), "Ces informations sont confidentielles et destin\u00E9es \u00E0 votre usage personnel."] })] })] }), _jsx("div", { className: "grid grid-cols-1 md:grid-cols-3 gap-6", children: cards.map((card) => (_jsxs("div", { onClick: card.onClick, className: "cursor-pointer bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition p-6 flex flex-col justify-between", children: [_jsxs("div", { children: [_jsx("div", { className: "mb-4", children: card.icon }), _jsx("h2", { className: "text-xl font-medium text-gray-800 mb-2", children: card.title }), _jsx("p", { className: "text-gray-600", children: card.description })] }), _jsx("button", { onClick: (e) => {
                                e.stopPropagation();
                                card.onClick();
                            }, className: "mt-6 text-indigo-600 hover:underline self-start", children: card.actionText })] }, card.title))) })] }));
};
export default AccountManagementPage;
